/**
 * Fire-and-forget client-side error reporter.
 *
 * Errors are queued (max 10) and flushed every 5 s or on beforeunload.
 * Uses fetch with keepalive so the flush survives page teardown.
 * Skipped entirely in DEV so local development stays noise-free.
 */

import { useAuthStore } from '@/shared/store/authStore'
import { redactSensitiveText, redactUrl } from '@/shared/lib/redactUrl'

export interface ClientErrorPayload {
  message: string
  stack?: string
  url: string
  userAgent?: string
  errorBoundary?: boolean
  componentStack?: string
}

const SKIP_PATTERNS = [
  /ResizeObserver/,
  /Non-Error promise rejection captured/,
  /Script error\./,
]

const EXTENSION_SCHEMES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
]

const MAX_QUEUE = 10
const FLUSH_INTERVAL_MS = 5_000

// Resolved at build time from npm package version (see vite.config.ts)
const RELEASE_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined

const queue: ClientErrorPayload[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null

function shouldSkip(payload: ClientErrorPayload): boolean {
  if (SKIP_PATTERNS.some(p => p.test(payload.message))) return true
  if (payload.stack && EXTENSION_SCHEMES.some(s => payload.stack!.includes(s))) return true
  return false
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = useAuthStore.getState().accessToken
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

function sendNow(items: ClientErrorPayload[]): void {
  if (items.length === 0) return
  const headers = buildHeaders()
  for (const item of items) {
    // keepalive: true keeps the request alive through beforeunload
    fetch('/api/internal/client-error', {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...item, releaseVersion: RELEASE_VERSION }),
      keepalive: true,
    }).catch(() => {
      // Never propagate errors from the error reporter itself
    })
  }
}

function flush(): void {
  if (queue.length === 0) return
  const items = queue.splice(0, queue.length)
  sendNow(items)
}

/**
 * Strip credentials out of a report before it is queued.
 *
 * It happens HERE and not at the call sites on purpose: `window.onerror`, the
 * `unhandledrejection` listener and `ErrorBoundary.componentDidCatch` all hand
 * over `window.location.href` verbatim, and every future caller would have to
 * remember to redact. One choke point cannot be forgotten. Backlog R7.
 *
 * `message` and `stack` are scrubbed too — react-router's "No route matches URL
 * …" puts the page path inside the message, which would otherwise smuggle the
 * token past a `url`-only redaction.
 */
export function redactClientErrorPayload(payload: ClientErrorPayload): ClientErrorPayload {
  return {
    ...payload,
    url: redactUrl(payload.url),
    message: redactSensitiveText(payload.message),
    stack: redactSensitiveText(payload.stack),
    componentStack: redactSensitiveText(payload.componentStack),
  }
}

export function reportClientError(rawPayload: ClientErrorPayload): void {
  if (import.meta.env.DEV) return
  const payload = redactClientErrorPayload(rawPayload)
  if (shouldSkip(payload)) return
  if (queue.length >= MAX_QUEUE) return

  queue.push(payload)

  if (flushTimer === null) {
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flush()
    if (flushTimer !== null) {
      clearInterval(flushTimer)
      flushTimer = null
    }
  })
}
